import React from 'react';
import { Link } from 'react-router-dom';
import { animated, useTransition } from '@react-spring/web';

import { faArrowLeft } from '@fortawesome/pro-solid-svg-icons/faArrowLeft';
import { faChalkboardUser } from '@fortawesome/pro-solid-svg-icons/faChalkboardUser';
import { faCheck } from '@fortawesome/pro-solid-svg-icons/faCheck';
import { faCommentDots } from '@fortawesome/pro-solid-svg-icons/faCommentDots';
import { faPaperPlane } from '@fortawesome/pro-solid-svg-icons/faPaperPlane';
import { faSeedling } from '@fortawesome/pro-solid-svg-icons/faSeedling';
import { faSparkles } from '@fortawesome/pro-solid-svg-icons/faSparkles';
import { faTicket } from '@fortawesome/pro-solid-svg-icons/faTicket';
import { faUserGroup } from '@fortawesome/pro-solid-svg-icons/faUserGroup';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { useAnimatedRouteState } from '@wildidea/components/AnimatedRoutes/AnimatedRoutes';
import { LinkButton } from '@wildidea/components/Button/LinkButton';
import { Card } from '@wildidea/components/Card/Card';
import { useThemeAnimations } from '@wildidea/components/Theme/Theme';

import * as css from './AmbassadorsRoute.module.css';

const AMBASSADOR_BENEFITS = [
  'Use the app at a discount while you are part of the program',
  'Share $10 discounts with your students and qualified group members',
  'Early access to new features before they ship',
  'A direct line to the team for feedback and ideas'
];

const AMBASSADOR_EXPECTATIONS = [
  'Introduce Rescue Ally to new students or peers regularly',
  'Share what you and your students think — what works, what is confusing, what is missing',
  'Help us understand the needs of the people who will rely on the app in the field'
];

const AmbassadorsRoute: React.FC<{}> = () => {
  const { phase, onTransitionEnd } = useAnimatedRouteState();
  const active = phase === 'enter' || phase === 'active';
  const { fromBelow } = useThemeAnimations('Common');
  const contentTransition = useTransition(active, { ...fromBelow, onRest: onTransitionEnd });
  return contentTransition((style, active) =>
    active ? (
      <animated.div style={style} className={css.layout}>
        <div className={css.pageHeader}>
          <Link to="/" aria-label="Back">
            <FontAwesomeIcon icon={faArrowLeft} />
          </Link>
          <h1>Ambassadors</h1>
        </div>

        <section className={css.hero}>
          <h2>Rescue Ally Ambassadors</h2>
          <div className={css.heroSub}>
            <div>
              A program for wilderness medicine instructors, search and rescue trainers, and other
              leaders who introduce the app to new people every month.
            </div>
          </div>
        </section>

        <section className={css.pilot}>
          <Card color="paper1" className={css.pilotCard}>
            <div className={css.pilotIcon}>
              <FontAwesomeIcon icon={faSeedling} />
            </div>
            <h3>Be a part of something new</h3>
            <p>
              The Ambassador program is in its pilot phase. We are starting with a small group of
              people with expertise in austere medicine. The details below may evolve as we learn
              what works.
            </p>
            <p>If this sounds like you, we would love to hear from you.</p>
            <LinkButton
              appearance="fill"
              color="info"
              href="mailto:ambassadors@rescueally.org"
              icon={<FontAwesomeIcon icon={faPaperPlane} />}
              className={css.pilotCta}
            >
              Reach Out Today
            </LinkButton>
          </Card>
        </section>

        <section className={css.section}>
          <div className={css.sectionIcon}>
            <FontAwesomeIcon icon={faChalkboardUser} />
          </div>
          <h2>Who this is for</h2>
          <p>
            Ambassadors are people who already have an audience of folks who would benefit from
            Rescue Ally — and who are well positioned to teach them how to use it well.
          </p>
          <p>The best fit looks something like:</p>
          <ul className={css.featureList}>
            <li>
              <FontAwesomeIcon icon={faCheck} className={css.checkIcon} />
              <span>Wilderness medicine instructors (WFA, WFR, WEMT, and similar)</span>
            </li>
            <li>
              <FontAwesomeIcon icon={faCheck} className={css.checkIcon} />
              <span>Search and rescue trainers and team educators</span>
            </li>
            <li>
              <FontAwesomeIcon icon={faCheck} className={css.checkIcon} />
              <span>
                Anyone who runs courses, programs, or groups with a regular flow of new participants
              </span>
            </li>
            <li>
              <FontAwesomeIcon icon={faCheck} className={css.checkIcon} />
              <span>People who can offer informed, ongoing feedback from the field</span>
            </li>
          </ul>
        </section>

        <section className={css.section}>
          <div className={css.sectionIcon}>
            <FontAwesomeIcon icon={faSparkles} />
          </div>
          <h2>What ambassadors get</h2>
          <ul className={css.featureList}>
            {AMBASSADOR_BENEFITS.map((benefit) => (
              <li key={benefit}>
                <FontAwesomeIcon icon={faCheck} className={css.checkIcon} />
                <span>{benefit}</span>
              </li>
            ))}
          </ul>
        </section>

        <section className={css.section}>
          <div className={css.sectionIcon}>
            <FontAwesomeIcon icon={faCommentDots} />
          </div>
          <h2>What we ask in return</h2>
          <ul className={css.featureList}>
            {AMBASSADOR_EXPECTATIONS.map((expectation) => (
              <li key={expectation}>
                <FontAwesomeIcon icon={faCheck} className={css.checkIcon} />
                <span>{expectation}</span>
              </li>
            ))}
          </ul>
          <p className={css.sectionNote}>
            Ambassadors are not salespeople. We do not have quotas. We just want the app in the
            hands of people who will actually use it well, and we want to hear what they think.
          </p>
        </section>

        <section className={css.section}>
          <div className={css.sectionIcon}>
            <FontAwesomeIcon icon={faTicket} />
          </div>
          <h2>About the discount</h2>
          <p>
            The $10 discount is meant to be a perk you pass along to your <strong>students</strong>{' '}
            — or anyone who completes your training or otherwise earns a place in your group.
          </p>
          <p>
            Think of it as a pro deal: a benefit your participants earn through real engagement, not
            a coupon code to hand out broadly. Used this way, the discount stays meaningful, and it
            adds value to the programs you already run.
          </p>
          <p className={css.sectionNote}>
            We are still working out exactly how this will be administered. As an ambassador, you
            will help us shape it.
          </p>
        </section>

        <section className={css.section}>
          <div className={css.sectionIcon}>
            <FontAwesomeIcon icon={faUserGroup} />
          </div>
          <h2>SAR teams and Pro / Team Accounts</h2>
          <p>
            If you lead a search and rescue team or run a professional rescue organization, the
            Ambassador program is probably not the right fit for what you need. We are planning a
            separate <strong>Pro / Team Accounts</strong> program for organizations that want shared
            assessments, team management, custom protocols, and other team-focused features.
          </p>
          <p>
            That program does not exist yet. We are talking with teams right now to understand what
            would actually be useful before we build it. If that sounds like you, please reach out —
            we would love to include your team in the conversation.
          </p>
        </section>

        <section className={css.bottomCta}>
          <LinkButton
            appearance="fill"
            color="info"
            href="mailto:ambassadors@rescueally.org"
            icon={<FontAwesomeIcon icon={faPaperPlane} />}
            className={css.bottomCtaButton}
          >
            Reach Out Today
          </LinkButton>
        </section>

        <footer className={css.footer}>
          <p>
            Details of the program may change as the pilot evolves. See our{' '}
            <Link to="/terms">Terms of Service</Link> for general account terms, and our{' '}
            <Link to="/pricing">Pricing</Link> page for standard purchase options.
          </p>
        </footer>
      </animated.div>
    ) : null
  );
};

export default AmbassadorsRoute;
